import { Controller } from '@hotwired/stimulus'

/**
 * FilterManagerController
 *
 * This controller lets you display only relevant filter UI elements:
 * - Selecting a filter displays "input group" (pre-rendered on page and hidden
 *   by default)
 * - Edit toggles between "applied badge" state (showing the current filter value)
 *   and "input group" state (allowing user to modify the filter)
 * - Automatic focus on select elements when activated
 */

export default class extends Controller {
    static targets = [
        'filter', // applied filters/_chip partial
        'filterForm', // filters/_form partial for adding/editing filter
        'filterFormInput', // <input>s inside the filter form (excluding <select>)
        'filterGroup', // container for both filter and filter form
        'applyButton', // apply button only shown when there are visible filter forms
    ]

    showFilter(event) {
        event.preventDefault()
        const { filterKey } = event.currentTarget.dataset
        if (filterKey) {
            this.#showFilterForm(filterKey)
        }
    }

    editFilter(event) {
        event.preventDefault()
        const { filterKey } = event.currentTarget.dataset
        if (filterKey) {
            this.#showFilterForm(filterKey)
        }
    }

    cancelFilter(event) {
        event.preventDefault()
        const { filterKey } = event.currentTarget.dataset
        if (filterKey) {
            const filterForm = this.#getFilterForm(filterKey)
            if (filterForm) {
                // Hide the filter form
                filterForm.classList.add('d-none')
                // Clear the input values (disabling is not enough)
                filterForm.querySelectorAll('input, select').forEach((input) => {
                    input.value = ''
                })
            }
            this.#toggleInputDisabled(filterKey, true)
            this.#toggleApplyButton()
        }
    }

    #showFilterForm(filterKey) {
        const filterForm = this.#getFilterForm(filterKey)
        if (!filterForm) return

        filterForm.classList.remove('d-none')

        this.#toggleInputDisabled(filterKey)
        this.#hideFilter(filterKey)
        this.#toggleApplyButton()

        // Defer focus until after the next repaint to ensure the element is visible.
        requestAnimationFrame(() => {
            const select = filterForm.querySelector('select')
            if (select) {
                const tomSelectControl = filterForm.querySelector('.ts-control')
                if (tomSelectControl) {
                    tomSelectControl.click() // Use Tom Select's API to open
                } else {
                    select.focus()
                }
            } else {
                const input = filterForm.querySelector('input')
                if (input) {
                    input.focus()
                }
            }
        })
    }

    #hideFilter(filterKey) {
        const filter = this.#getFilter(filterKey)
        if (filter) {
            filter.classList.add('d-none')
        }
    }

    // Remove input[disabled] attribute when a filter <input> is in use.
    // We don't want to send empty values to the server which is why we disable
    // the input by default.
    #toggleInputDisabled(filterKey, disabled = false) {
        this.filterFormInputTargets.forEach((input) => {
            if (input.name === `${filterKey}[]`) {
                input.disabled = disabled
            }
        })
    }

    #toggleApplyButton() {
        if (!this.hasApplyButtonTarget) return

        const hasVisibleFilterForms = this.filterFormTargets.some(
            (group) => !group.classList.contains('d-none'),
        )
        this.applyButtonTarget.classList.toggle('d-none', !hasVisibleFilterForms)
    }

    #getFilterGroup(filterKey) {
        return this.filterGroupTargets.find((group) => group.dataset.filterKey === filterKey)
    }

    #getFilterForm(filterKey) {
        return this.#getFilterGroup(filterKey)?.querySelector(
            '[data-filter-manager-target="filterForm"]',
        )
    }

    #getFilter(filterKey) {
        return this.#getFilterGroup(filterKey)?.querySelector(
            '[data-filter-manager-target="filter"]',
        )
    }
}
